import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAsyncData } from "@/hooks/useAsyncData";
import { listAll, removeItem, updateItem } from "@/lib/adminRepository";
import { ADMIN } from "@/constants/testIds";
import { Plus, Pencil, Trash2, Newspaper, Tags } from "lucide-react";

const millis = (x) => x.publishAt?.toMillis?.() ?? 0;
const byNewest = (a, b) =>
  millis(b) - millis(a) || String(b.date || "").localeCompare(String(a.date || ""));

// Dedicated blog manager (MeProperty-style list), themed for A Homely Abode.
export default function BlogList() {
  const { data: rows, loading, error, reload } = useAsyncData(() => listAll("blogs"), []);
  const items = [...(rows || [])].sort(byNewest);

  const del = async (item) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete post "${item.title || item.id}"? This cannot be undone.`)) return;
    try {
      await removeItem("blogs", item.id);
      toast.success("Deleted");
      reload();
    } catch {
      toast.error("Delete failed");
    }
  };

  const togglePublish = async (item) => {
    const next = item.status === "published" ? "draft" : "published";
    await updateItem("blogs", item.id, { status: next });
    reload();
  };

  return (
    <div data-testid="admin-blogs">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="eyebrow text-bronze mb-1">Content</div>
          <h1 className="font-serif text-3xl text-ink">Blog Posts</h1>
          <p className="text-sm text-olive mt-1">Articles on /resources/blogs. Drafts are hidden from the public site.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/blogs/categories" data-testid="admin-blog-categories-link" className="btn-secondary inline-flex items-center gap-2">
            <Tags size={16} /> Categories
          </Link>
          <Link to="/admin/blogs/new" data-testid={ADMIN.newButton} className="btn-primary inline-flex items-center gap-2">
            <Plus size={16} /> New post
          </Link>
        </div>
      </div>

      {loading && <p className="text-olive">Loading…</p>}
      {error && <p className="text-red-700">Failed to load. <button className="underline" onClick={reload}>Retry</button></p>}

      {!loading && !error && (
        <table className="w-full text-sm border border-bronze/20 bg-cream" data-testid={ADMIN.list}>
          <thead className="bg-beige/60 text-left text-[0.65rem] uppercase tracking-wider text-olive">
            <tr>
              <th className="p-3 w-20">Cover</th>
              <th className="p-3">Post</th>
              <th className="p-3 w-40">Category</th>
              <th className="p-3 w-32">Date</th>
              <th className="p-3 w-24">Visibility</th>
              <th className="p-3 w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-t border-bronze/10 align-middle" data-testid={`admin-row-${item.id}`}>
                <td className="p-2">
                  <div className="h-12 w-14 overflow-hidden bg-beige border border-bronze/20 flex items-center justify-center">
                    {item.image ? <img src={item.image} alt="" className="h-full w-full object-cover" /> : <Newspaper size={16} className="text-bronze/50" />}
                  </div>
                </td>
                <td className="p-3">
                  <div className="font-medium text-ink">{item.title || item.id}</div>
                  <div className="text-xs text-olive">/{item.slug || item.id}</div>
                </td>
                <td className="p-3 text-xs text-ink_soft">{item.category || "—"}</td>
                <td className="p-3 text-xs text-ink_soft" data-testid={`blog-date-${item.id}`}>{item.date || "—"}</td>
                <td className="p-3">
                  <button
                    onClick={() => togglePublish(item)}
                    data-testid={ADMIN.publishToggle}
                    className={`px-2 py-1 text-[0.6rem] uppercase tracking-wider ${item.status === "published" ? "bg-bronze text-cream" : "bg-taupe/40 text-ink"}`}
                  >
                    {item.status || "draft"}
                  </button>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <Link to={`/admin/blogs/${item.id}`} className="text-bronze hover:text-bronze_dark" title="Edit"><Pencil size={15} /></Link>
                    <button onClick={() => del(item)} data-testid={ADMIN.deleteButton} className="text-red-600 hover:text-red-800" title="Delete"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-olive">No posts yet — click “New post”.</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
